import { useMemo } from "react";
import classNames from "classnames";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { CollapsibleViewButton } from "@/features/collapsible-view";
import type { FeatureContainerLayout, FormLevelFeature } from "@/entities/form-metadata";
import { FormLevelFeatureType, useMetadataQuery } from "@/entities/form-metadata";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { Grid } from "@/shared/components/Grid";
import { useErrorBoundaryLogger } from "@/shared/lib/fci";
import { AdvancedSearchLink } from "./AdvancedSearchLink/AdvancedSearchLink";
import { CategoryBucket } from "./CategoryBucket/CategoryBucket";
import { ClearSection } from "./Clear/ClearSection";
import { CollectionFocus } from "./CollectionFocus/CollectionFocus";
import { LimitToUserCountry } from "./LimitToUserCountry/LimitToUserCountry";
import { MatchAllTerms } from "./MatchAllTerms/MatchAllTerms";
import { Message } from "./Message/Message";
import { RotatedRecords } from "./RotatedRecords";
import { SearchAssistModal } from "./SearchAssistModal";
import { SubmitSection } from "./Submit/SubmitSection";
import { Timespan } from "./Timespan";
import { isNonVisual } from "./utils";
import styles from "./FeatureContainerRenderer.module.scss";

interface FeatureComponentMapperProps {
    feature: FormLevelFeature;
}
const FeatureComponentMapper = ({ feature }: FeatureComponentMapperProps) => {
    const logger = useErrorBoundaryLogger();

    switch (feature.featureType) {
        // Essential features — errors propagate to RemoteErrorBoundary
        case FormLevelFeatureType.CollapsibleView:
            return <CollapsibleViewButton {...feature.attributes} />;
        case FormLevelFeatureType.SubmitBtn:
            return <SubmitSection {...feature.attributes} />;
        case FormLevelFeatureType.CategoryBuckets:
            return <CategoryBucket {...feature.attributes} />;

        // Non-essential features — wrapped in ErrorBoundary to prevent
        // a crash in one feature from taking down the entire form
        case FormLevelFeatureType.Message:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <Message {...feature.attributes} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.ClearBtn:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <ClearSection {...feature.attributes} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.AdvancedSearchLink:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <AdvancedSearchLink {...feature.attributes} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.LimitToCountry:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <LimitToUserCountry {...feature.attributes} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.CollectionFocus:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <CollectionFocus {...feature.attributes} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.SearchAssistModal:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <SearchAssistModal {...feature.attributes} />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.RotatedRecords:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <RotatedRecords />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.MatchAllTerms:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <MatchAllTerms />
                </ErrorBoundary>
            );
        case FormLevelFeatureType.Timespan:
            return (
                <ErrorBoundary onError={logger(FCI_ERROR.Supplemental)}>
                    <Timespan {...feature.attributes} />
                </ErrorBoundary>
            );
        default:
            return null;
    }
};

interface FeatureContainerRendererProps {
    uuids: string[];
    layout?: FeatureContainerLayout;
    containerClassName?: string;
}

export const FeatureContainerRenderer = ({ uuids, layout, containerClassName }: FeatureContainerRendererProps) => {
    const { data: formMetadata } = useMetadataQuery();

    const features = useMemo(() => {
        const featuresMap = new Map(formMetadata.features.map((f) => [f.UUID, f]));
        return uuids.map((uuid) => featuresMap.get(uuid)!);
    }, [uuids, formMetadata.features]);

    if (isNonVisual(features)) {
        return <FeatureComponentMapper feature={features[0]} />;
    }

    return (
        <div data-test-sfs-container-type="featureContainer" className={containerClassName}>
            <Grid.Container>
                <Grid.Field spanDesktop={12} spanMobile={12} spanTablet={12}>
                    <div className={classNames({ [styles.horizontal as string]: layout === "horizontal" })}>
                        {features.map((feature) => (
                            <div key={feature.UUID} data-test-sfs-feature-type={feature.featureType}>
                                <FeatureComponentMapper feature={feature} />
                            </div>
                        ))}
                    </div>
                </Grid.Field>
            </Grid.Container>
        </div>
    );
};
